/**
 * Project hub → "Embed" tab (track B3).
 *
 * Issues, lists, edits and revokes the keys that authorize an ANONYMOUS
 * chat surface on a third-party website.
 *
 * Three things this component exists to make hard to get wrong:
 *
 *  1. THE PLAINTEXT IS SHOWN ONCE. Issuance opens a panel with the key
 *     and the paste-ready snippet plus an explicit "will not be shown
 *     again" warning. Only `sha256(secret)` is stored server-side, so a
 *     lost key is re-issued, never recovered.
 *  2. ORIGINS ARE EXACT, AND AT LEAST ONE IS REQUIRED. The chip editor
 *     normalizes client-side (mirroring the server) and refuses `*` /
 *     `null` / paths. Submitting with zero origins is blocked — an empty
 *     list is NOT "allow all", it is a key no browser can use.
 *  3. `allow_free_sql` IS AN OFF-BY-DEFAULT SWITCH WITH A RED WARNING.
 *     Turning it on lets an anonymous visitor's question drive arbitrary
 *     SELECTs over the project's DuckDB (inside the sql_guard limits).
 *     The helper text says so in plain language.
 */

import { Badge, Button, Card, CardBody, Input, Select } from "@omnitok/ui";
import { Spinner } from "@pervasivemind/bi-components";
import {
  AlertTriangle,
  Check,
  Copy,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { useT } from "@/i18n";
import {
  DEFAULT_DAILY_MESSAGE_QUOTA,
  MAX_TOOL_ITERATIONS,
  embedApi,
  normalizeOrigin,
  type EmbedKey,
  type EmbedKeyIssued,
} from "@/lib/embed-api";
import type { Project, ReportDraft } from "@/types";

interface Props {
  project: Project;
  /** Reports in this project — used to offer the chat contexts. */
  drafts: ReportDraft[];
}

interface FormState {
  label: string;
  clientKey: string;
  chatContextKey: string;
  origins: string[];
  originDraft: string;
  allowFreeSql: boolean;
  maxToolIterations: number;
  /** Empty string = the operator explicitly chose "no quota". */
  dailyQuota: string;
}

function emptyForm(): FormState {
  return {
    label: "",
    clientKey: "",
    chatContextKey: "",
    origins: [],
    originDraft: "",
    allowFreeSql: false,
    maxToolIterations: 4,
    dailyQuota: String(DEFAULT_DAILY_MESSAGE_QUOTA),
  };
}

/**
 * A report's chat context key is `report.key` with underscores turned
 * into dashes; the whole-project context is `all-{slug}`. Both mirror
 * the backend seeding, so the operator picks from a list instead of
 * typing a string that would 403 at first use.
 */
function contextOptions(project: Project, drafts: ReportDraft[]): string[] {
  const perReport = drafts.map((d) => d.key.replace(/_/g, "-"));
  return [`all-${project.slug}`, ...perReport];
}

export function ProjectEmbedPane({ project, drafts }: Props) {
  const { t } = useT();
  const [keys, setKeys] = useState<EmbedKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [creating, setCreating] = useState(false);
  const [issued, setIssued] = useState<EmbedKeyIssued | null>(null);
  const [copied, setCopied] = useState<"key" | "snippet" | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await embedApi.list(project.slug);
      setKeys(res.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [project.slug]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const addOrigin = () => {
    const normalized = normalizeOrigin(form.originDraft);
    if (normalized === null) {
      setError(t("embed.error.badOrigin"));
      return;
    }
    setError(null);
    setForm((f) => ({
      ...f,
      originDraft: "",
      origins: f.origins.includes(normalized)
        ? f.origins
        : [...f.origins, normalized],
    }));
  };

  const removeOrigin = (origin: string) =>
    setForm((f) => ({ ...f, origins: f.origins.filter((o) => o !== origin) }));

  const canSubmit =
    form.label.trim() !== "" &&
    form.clientKey.trim() !== "" &&
    form.chatContextKey.trim() !== "" &&
    // Rule 1 at the UI layer too: zero origins is never submittable.
    form.origins.length > 0;

  const submit = async () => {
    if (!canSubmit) return;
    setCreating(true);
    setError(null);
    try {
      const quotaText = form.dailyQuota.trim();
      const res = await embedApi.issue(project.slug, {
        label: form.label.trim(),
        client_key: form.clientKey.trim(),
        chat_context_key: form.chatContextKey.trim(),
        allowed_origins: form.origins,
        allow_free_sql: form.allowFreeSql,
        max_tool_iterations: form.maxToolIterations,
        daily_message_quota: quotaText === "" ? null : Number(quotaText),
      });
      setIssued(res);
      setForm(emptyForm());
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (key: EmbedKey) => {
    setBusyId(key.id);
    setError(null);
    try {
      await embedApi.revoke(key.id);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  };

  const toggleFreeSql = async (key: EmbedKey) => {
    setBusyId(key.id);
    setError(null);
    try {
      await embedApi.update(key.id, { allow_free_sql: !key.allow_free_sql });
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  };

  const copy = async (text: string, what: "key" | "snippet") => {
    await navigator.clipboard.writeText(text);
    setCopied(what);
    setTimeout(() => setCopied(null), 2000);
  };

  const contexts = contextOptions(project, drafts);

  return (
    <div className="space-y-4">
      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-error/40 bg-error/5 px-4 py-2 text-sm text-error-dark">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* ── Post-issuance panel: the ONE time the plaintext exists ── */}
      {issued && (
        <Card variant="bordered">
          <CardBody>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-neutral-900">
                  {t("embed.issued.title")}
                </h3>
                <p className="mt-1 flex items-start gap-1.5 text-xs text-warning-dark">
                  <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                  {t("embed.issued.warning")}
                </p>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setIssued(null)}
                aria-label={t("common.close")}
              >
                <X size={14} />
              </Button>
            </div>

            <label className="mt-4 block text-xs font-medium text-neutral-600">
              {t("embed.issued.key")}
            </label>
            <div className="mt-1 flex items-center gap-2">
              <code className="flex-1 overflow-x-auto rounded border border-neutral-200 bg-neutral-50 px-3 py-2 text-xs">
                {issued.key_plaintext}
              </code>
              <Button
                size="sm"
                variant="outline"
                aria-label={t("common.copy")}
                onClick={() => void copy(issued.key_plaintext, "key")}
              >
                {copied === "key" ? <Check size={14} /> : <Copy size={14} />}
              </Button>
            </div>

            <label className="mt-4 block text-xs font-medium text-neutral-600">
              {t("embed.issued.snippet")}
            </label>
            <div className="mt-1 flex items-start gap-2">
              <pre className="flex-1 overflow-x-auto rounded border border-neutral-200 bg-neutral-50 px-3 py-2 text-xs leading-relaxed">
                {issued.snippet_html}
              </pre>
              <Button
                size="sm"
                variant="outline"
                aria-label={t("common.copy")}
                onClick={() => void copy(issued.snippet_html, "snippet")}
              >
                {copied === "snippet" ? (
                  <Check size={14} />
                ) : (
                  <Copy size={14} />
                )}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* ── Issue form ─────────────────────────────────────────── */}
      <Card variant="bordered">
        <CardBody>
          <h3 className="text-sm font-semibold text-neutral-900">
            {t("embed.form.title")}
          </h3>
          <p className="mt-1 text-xs text-neutral-500">
            {t("embed.form.help")}
          </p>

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Input
              label={t("embed.field.label")}
              value={form.label}
              onChange={(e) =>
                setForm((f) => ({ ...f, label: e.target.value }))
              }
              placeholder={t("embed.field.labelPlaceholder")}
            />
            <Input
              label={t("embed.field.clientKey")}
              value={form.clientKey}
              onChange={(e) =>
                setForm((f) => ({ ...f, clientKey: e.target.value }))
              }
              placeholder="hp"
            />
            <div>
              <Select
                label={t("embed.field.chatContextKey")}
                options={[
                  { value: "", label: t("embed.field.chatContextPick") },
                  ...contexts.map((c) => ({ value: c, label: c })),
                ]}
                value={form.chatContextKey}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setForm((f) => ({ ...f, chatContextKey: e.target.value }))
                }
                fullWidth
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-neutral-700">
                {t("embed.field.maxToolIterations")}
              </label>
              <input
                type="number"
                min={1}
                max={MAX_TOOL_ITERATIONS}
                className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm"
                value={form.maxToolIterations}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    maxToolIterations: Number(e.target.value),
                  }))
                }
              />
              <p className="mt-1 text-xs text-neutral-500">
                {t("embed.field.maxToolIterationsHelp")}
              </p>
            </div>
          </div>

          {/* Origins — chip editor */}
          <div className="mt-4">
            <label className="mb-1 block text-sm font-medium text-neutral-700">
              {t("embed.field.origins")}
            </label>
            <div className="flex gap-2">
              <input
                className="flex-1 rounded-lg border border-neutral-300 px-3 py-2 text-sm"
                value={form.originDraft}
                placeholder="https://www.customer.example"
                onChange={(e) =>
                  setForm((f) => ({ ...f, originDraft: e.target.value }))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addOrigin();
                  }
                }}
              />
              <Button
                size="sm"
                variant="outline"
                aria-label={t("embed.field.originAdd")}
                onClick={addOrigin}
              >
                <Plus size={14} />
              </Button>
            </div>
            <p className="mt-1 text-xs text-neutral-500">
              {t("embed.field.originsHelp")}
            </p>
            {form.origins.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {form.origins.map((o) => (
                  <span
                    key={o}
                    className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-neutral-50 px-2.5 py-1 text-xs"
                  >
                    {o}
                    <button
                      type="button"
                      onClick={() => removeOrigin(o)}
                      className="text-neutral-500 hover:text-error-dark"
                      aria-label={t("common.remove")}
                    >
                      <X size={11} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Quota */}
          <div className="mt-4 sm:w-1/2">
            <label className="mb-1 block text-sm font-medium text-neutral-700">
              {t("embed.field.dailyQuota")}
            </label>
            <input
              type="number"
              min={1}
              className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm"
              value={form.dailyQuota}
              onChange={(e) =>
                setForm((f) => ({ ...f, dailyQuota: e.target.value }))
              }
            />
            <p className="mt-1 text-xs text-neutral-500">
              {t("embed.field.dailyQuotaHelp")}
            </p>
          </div>

          {/* allow_free_sql — the security-critical switch */}
          <div className="mt-4 rounded-lg border border-error/30 bg-error/5 px-3 py-2.5">
            <label className="flex cursor-pointer items-start gap-2">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={form.allowFreeSql}
                onChange={(e) =>
                  setForm((f) => ({ ...f, allowFreeSql: e.target.checked }))
                }
              />
              <span>
                <span className="block text-sm font-medium text-neutral-800">
                  {t("embed.field.allowFreeSql")}
                </span>
                <span className="mt-0.5 block text-xs text-error-dark">
                  {t("embed.field.allowFreeSqlWarning")}
                </span>
              </span>
            </label>
          </div>

          <div className="mt-4 flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => void submit()}
              disabled={!canSubmit || creating}
            >
              {creating ? <Spinner size={14} /> : <Plus size={14} />}
              {t("embed.form.submit")}
            </Button>
            {!canSubmit && (
              <span className="text-xs text-neutral-500">
                {t("embed.form.incomplete")}
              </span>
            )}
          </div>
        </CardBody>
      </Card>

      {/* ── Existing keys ──────────────────────────────────────── */}
      <Card variant="bordered" noPadding>
        {loading ? (
          <div className="flex justify-center py-10">
            <Spinner size={18} />
          </div>
        ) : keys.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-neutral-500">
            {t("embed.list.empty")}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-neutral-200 bg-neutral-50 text-left text-xs text-neutral-500">
              <tr>
                <th className="px-4 py-2">{t("embed.col.label")}</th>
                <th className="px-4 py-2">{t("embed.col.scope")}</th>
                <th className="px-4 py-2">{t("embed.col.origins")}</th>
                <th className="px-4 py-2">{t("embed.col.policy")}</th>
                <th className="px-4 py-2">{t("embed.col.usage")}</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr
                  key={k.id}
                  className="border-b border-neutral-100 last:border-0"
                >
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-neutral-900">
                      {k.label}
                    </div>
                    <code className="text-xs text-neutral-500">
                      {k.key_prefix}…
                    </code>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-neutral-600">
                    {k.client_key} / {k.chat_context_key}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-neutral-600">
                    {k.allowed_origins.join(", ")}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-wrap items-center gap-1">
                      {/* Status carries an explicit WORD, not colour
                          alone — the operator is red-green colourblind. */}
                      <Badge variant={k.enabled ? "success" : "neutral"}>
                        {k.enabled
                          ? t("embed.status.active")
                          : t("embed.status.revoked")}
                      </Badge>
                      {k.allow_free_sql && (
                        <Badge variant="error">
                          {t("embed.badge.freeSql")}
                        </Badge>
                      )}
                      <span className="text-xs text-neutral-500">
                        {t("embed.col.iterations", {
                          n: k.max_tool_iterations,
                        })}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-neutral-600">
                    <div>
                      {t("embed.col.total", { n: k.use_count })}
                    </div>
                    <div className="text-neutral-500">
                      {k.daily_message_quota === null
                        ? t("embed.quota.none")
                        : `${k.quota_day_count}/${k.daily_message_quota}`}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {k.enabled && (
                        <>
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={busyId === k.id}
                            onClick={() => void toggleFreeSql(k)}
                          >
                            {k.allow_free_sql
                              ? t("embed.action.disableFreeSql")
                              : t("embed.action.enableFreeSql")}
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={busyId === k.id}
                            onClick={() => void revoke(k)}
                            aria-label={t("embed.action.revoke")}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
